/**
 * Arquivo: src/components/MoneyInput.tsx
 * Versão: 5.6.5
 * Data: 29/09/2026
 * Descrição: Campo monetário brasileiro com máscara fixa de duas casas decimais e calculadora integrada ao próprio campo.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Calculator, Delete, Equal, X } from 'lucide-react'

type Props = {
  value: number
  onChange: (value:number)=>void
  placeholder?: string
  disabled?: boolean
}

const arredondar=(n:number)=>Math.round((Number(n)+Number.EPSILON)*100)/100
const moedaSemSimbolo=(n:number)=>Math.abs(Number.isFinite(n)?arredondar(n):0).toLocaleString('pt-BR',{
  minimumFractionDigits:2,
  maximumFractionDigits:2,
})
const exprValor=(n:number)=>Number.isFinite(n)&&Number(n)!==0
  ? arredondar(Math.abs(n)).toFixed(2).replace('.',',')
  : ''

const valorMascarado=(entrada:string)=>{
  const digitos=String(entrada||'').replace(/\D/g,'')
  if(!digitos)return 0
  return arredondar(Number(digitos)/100)
}

const calc=(expr:string)=>{
  const safe=expr.replace(/,/g,'.').replace(/×/g,'*').replace(/÷/g,'/')
  if(!safe.trim()||!/^[0-9+\-*/().\s]+$/.test(safe)) throw new Error('Expressão inválida')
  const tokens=safe.match(/\d+(?:\.\d+)?|[()+\-*/]/g)||[]
  let i=0
  const primary=():number=>{
    const t=tokens[i++]
    if(t==='('){
      const v=add()
      if(tokens[i++]!==')')throw new Error('Parênteses inválidos')
      return v
    }
    if(t==='-')return-primary()
    const n=Number(t)
    if(!Number.isFinite(n))throw new Error('Número inválido')
    return n
  }
  const mul=()=>{
    let v=primary()
    while(tokens[i]==='*'||tokens[i]==='/'){
      const op=tokens[i++],b=primary()
      if(op==='/'&&b===0)throw new Error('Divisão por zero')
      v=op==='*'?v*b:v/b
    }
    return v
  }
  const add=()=>{
    let v=mul()
    while(tokens[i]==='+'||tokens[i]==='-'){
      const op=tokens[i++],b=mul()
      v=op==='+'?v+b:v-b
    }
    return v
  }
  const r=add()
  if(i!==tokens.length||!Number.isFinite(r))throw new Error('Expressão inválida')
  return r
}

export default function MoneyInput({value,onChange,placeholder='0,00',disabled=false}:Props){
  const [open,setOpen]=useState(false)
  const [expr,setExpr]=useState(exprValor(value))
  const [erro,setErro]=useState('')
  const [texto,setTexto]=useState(moedaSemSimbolo(value))
  const ref=useRef<HTMLDivElement>(null)

  useEffect(()=>{
    setTexto(moedaSemSimbolo(value))
    if(!open)setExpr(exprValor(value))
  },[value,open])

  useEffect(()=>{
    const close=(e:MouseEvent)=>{
      if(ref.current&&!ref.current.contains(e.target as Node))setOpen(false)
    }
    document.addEventListener('mousedown',close)
    return()=>document.removeEventListener('mousedown',close)
  },[])

  const resultado=useMemo(()=>{
    try{return Math.abs(arredondar(calc(expr)))}
    catch{return null}
  },[expr])

  const alterarTexto=(entrada:string)=>{
    const n=valorMascarado(entrada)
    setTexto(moedaSemSimbolo(n))
    onChange(n)
  }

  const aplicar=()=>{
    if(resultado===null){
      setErro('Conta inválida')
      return
    }
    onChange(resultado)
    setTexto(moedaSemSimbolo(resultado))
    setExpr(exprValor(resultado))
    setErro('')
    setOpen(false)
  }

  const key=(k:string)=>{
    if(k==='C'){setExpr('');setErro('');return}
    if(k==='⌫'){setExpr(x=>x.slice(0,-1));setErro('');return}
    if(k==='='){aplicar();return}
    setExpr(x=>x+k)
    setErro('')
  }

  return <div className={`money-input ${disabled?'is-disabled':''}`} ref={ref}>
    <div className="money-input-field">
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        disabled={disabled}
        value={texto}
        placeholder={placeholder}
        onFocus={e=>requestAnimationFrame(()=>e.currentTarget.select())}
        onChange={e=>alterarTexto(e.target.value)}
        aria-label="Valor em reais"
      />
      <button
        type="button"
        className="money-calc-trigger"
        title="Calcular valor"
        aria-label="Abrir calculadora do valor"
        disabled={disabled}
        onClick={()=>{setExpr(exprValor(value));setErro('');setOpen(x=>!x)}}
      >
        <Calculator size={18}/>
      </button>
    </div>

    {open&&!disabled&&<div className="money-calculator" onKeyDown={e=>{
      if(e.key==='Escape'){e.preventDefault();setOpen(false);return}
      if(e.key==='Enter'){e.preventDefault();aplicar()}
    }}>
      <div className="money-calc-header">
        <span>Calculadora do valor</span>
        <button type="button" className="money-calc-close" title="Fechar calculadora" aria-label="Fechar calculadora" onClick={()=>setOpen(false)}>
          <X size={16}/>
        </button>
      </div>

      <input
        autoFocus
        className="money-expression"
        value={expr}
        placeholder="Ex.: 1250 + 89,90"
        onChange={e=>{setExpr(e.target.value.replace(/[^0-9,+\-*/().×÷\s]/g,''));setErro('')}}
        onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();aplicar()}}}
      />

      <div className="money-calc-result">
        <span>Resultado</span>
        <strong className="privacy-value">{resultado===null?'—':`R$ ${moedaSemSimbolo(resultado)}`}</strong>
      </div>

      {erro&&<small className="money-calc-error">{erro}</small>}

      <div className="money-calc-grid">
        {['7','8','9','÷','4','5','6','×','1','2','3','-','0',',','⌫','+'].map(k=>
          <button type="button" key={k} onClick={()=>key(k)}>
            {k==='⌫'?<Delete size={15}/>:k}
          </button>
        )}
        <button type="button" className="calc-clear" onClick={()=>key('C')}>C</button>
        <button type="button" className="calc-equal" onClick={()=>key('=')} disabled={resultado===null}>
          <Equal size={16}/> Usar no valor
        </button>
      </div>

      <small className="money-calc-help">O resultado entra diretamente no campo, sempre com duas casas decimais.</small>
    </div>}
  </div>
}
